import { Component, Inject } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { DateTimeHelper } from '../../../../helper/date-time.helper';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import { IActionStudioSearchResult } from '../../../../models/action.model';
import { AddressHelper } from "../../../../helper/address.helper";
import { ResourceCountry } from "../../../../models/resources/resources.model";
import { IUser } from '../../../../models/leads/leads.model';

@Component({
  selector: 'fi-external-email',
  templateUrl: './external-email.component.html',
  styleUrls: ['./external-email.component.scss'],
  standalone: false,
})
export class ExternalEmailComponent extends BaseCustomDialog {
  contactName = '';
  leadId: number;
  selectedTypeId: number;
  readonly dateOptionTypes = DateTimeHelper.datePeriodTypes;
  searchedText = '';
  searchTimeout = null;
  note = '';
  externalEmail: string;
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('LEAD_FORWARD_EXT', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('LEAD_FORWARD_EXT', true);
  submitting: boolean;
  formIsNotHidden = true;
  noEmailEntered = false;
  selectActive = false;
  selectedOption: 'EMAIL' | 'DEALER_PARTNER' = 'DEALER_PARTNER';
  searchResults: IActionStudioSearchResult[] = [];
  selectedPartner: IActionStudioSearchResult;
  noDealer = false;
  emailError: string;
  selectedValue: any;
  options: { name: string, id: number, byArea: boolean, value: IActionStudioSearchResult, image?: string }[] = [];

  private countries: ResourceCountry[];

  constructor(
    public dialogRef: MatDialogRef<ExternalEmailComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: {contactName: string, leadId: number, leadTypeId: number, title: string},
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService
  ) {
    super(dialogRef);

    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.title = dialogData?.title;

    AddressHelper.getCountries().then(item => this.countries = item);

  }

  onSelect(value: string): void {
    this.selectedValue = value;
    const item = this.options[+value];

    if (item) {
      this.selectResultItem(item.value);
    }
  }

  toggleSelect(event: Event): void {
    event.stopPropagation();
    this.selectActive = !this.selectActive;
    this.normalizeSearchText();
  }

  hideSelect(): void {
    this.selectActive = false;
    this.normalizeSearchText();
  }

  showSelect(event: Event): void {
    event.stopPropagation();
    this.selectActive = true;
  }

  doSearch(): void {
    const lastSearchText = this.searchedText;

    if (this.searchTimeout !== null) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.actionDataService.studioSearch(this.searchedText).subscribe(({ data, success }) => {
        if (success) {
          if (lastSearchText === this.searchedText) {
            this.searchResults = data;

            let index = 0;
            this.options = [];

            this.searchResults?.forEach(value => {
              value.Address.Country = this.countries.find(country => country.IsoCode === value.Address.CountryCode)?.Name;

              let name = '';

              if (value?.Name) {
                name += value?.Name + ' ';
              }

              const tooltip = this.getStudioItemTooltip(value);

              if (tooltip) {
                if (name) {
                  name += '\n';
                }

                name += tooltip + ' ';
              }

              if (value?.KKZ) {
                if (name) {
                  name += '\n';
                }

                name += value?.KKZ + ' ';
              }

              this.options.push({
                name,
                image: value?.AreaManager?.AvatarURL,
                id: index++,
                byArea: true,
                value,
              })
            });


          }
        }
      });
    }, 300)
  }

  private getStudioItemTooltip(item: IActionStudioSearchResult): string {
    let result = '';

    if (item?.KKZ && item?.ContactManagers?.length) {
      const manager = item.ContactManagers[0];
      result = `${manager.FirstName} ${manager.LastName}`;
    } else if (item?.Email) {
      result = item.Email;
    }

    return result;
  }

  selectResultItem(result: IActionStudioSearchResult): void {
    this.selectActive = false;
    this.selectedPartner = result;
    this.normalizeSearchText();
  }

  get showMultipleEmailError(): boolean {
    return this.selectedOption === 'EMAIL' && (this.externalEmail?.includes(',') || this.externalEmail?.includes(' '));
  }

  get showEmailError(): boolean {
    return this.selectedOption === 'EMAIL' && !!this.emailError;
  }

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  get noDealerSelected(): boolean {
    return this.selectedOption === 'DEALER_PARTNER' && !this.selectedPartner && this.noDealer;
  }

  get canSubmit(): boolean {

    if (!this.leadId) {
      return false;
    }

    if (this.selectedOption === 'EMAIL') {
      return this.formIsNotHidden && !!this.externalEmail?.trim();
    }

    if (this.selectedOption === 'DEALER_PARTNER') {
      return !!this.selectedPartner;
    }

    return true;
  }

  submit(): void {
    if (this.selectedOption === 'DEALER_PARTNER') {
      this.noDealer = !this.selectedPartner;

      if (this.selectedPartner) {
        this.submitting = true;

        this.actionDataService.sendKkz(this.leadId, this.note, this.selectedPartner.KKZ, this.selectedPartner.Email)
          .subscribe(({ success, data }) => {
            if (success) {
              this.clearClassAndCloseDialog(this.dialogRef, 'remove');
            }
            this.submitting = false;
          });
      }

      return;
    }

    const valid = this.externalEmail?.match(/\S+@\S+\.\S+/);
    if (!this.externalEmail || !valid) {
      this.noEmailEntered = true;
      return;
    }
    this.noEmailEntered = false;
    if (this.formIsNotHidden) {
      this.submitting = true;
      this.actionDataService.sendEmail(this.leadId, this.note, this.externalEmail)
        .subscribe(({ success, error }) => {
          if (success) {
            this.clearClassAndCloseDialog(this.dialogRef, 'remove');
          }
          if ((error as any)?.data) {
            this.emailError = (error as any)?.data;
          }
          this.submitting = false;
        });
    }
  }

  private normalizeSearchText(): void {
    setTimeout(() => {
      if (this.selectedPartner && !this.selectActive) {
        this.searchedText = this.selectedPartner.KKZ;
      }
    }, 50);
  }

  clearDealer(): void {
    this.searchResults = [];
    this.searchedText = '';
    this.selectedValue = null;
  }

  clearSelectedDealer(): void {
    this.selectedValue = null;
    this.selectActive = true;
    this.selectedPartner = undefined;
    this.emailError = '';
    this.normalizeSearchText();
  }
}
